/** @jsx isolateComponent */

import React, { useState } from 'react';

import { Colors, Animation } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import { uniqueId } from '../../../utils/uniqueId';

import { Interpolation } from '@emotion/core';

type Props = {
  /** The available color variations for the Indicator. */
  color: 'grey' | 'white';
  /** The number of bubbles to render in the Indicator. */
  count: number;
  /**
   * Accessibility label that describes the group of options available inside the Indicator
   * e.g. "Select a page", "List of slides", etc.
   */
  ariaLabel: string;
  /** Handles selection of new items in the list of bubbles. */
  onChangeIndex: (index: number) => void;
  /** The index of the selected bubble. */
  selectedIndex: number;
  /**
   * The descriptive term for each item in the list of bubbles
   * e.g. "page", "slide", "image", etc.
   * */
  noun?: string;
};

const colorVariations = {
  grey: {
    base: {
      borderColor: Colors.gray60,
    },
    check: {
      borderColor: Colors.blue500,
    },
    hover: {
      borderColor: Colors.blue500,
    },
    focus: {
      backgroundColor: Colors.blue500,
      borderColor: Colors.blue500,
    },
  },
  white: {
    base: {
      borderColor: Colors.white,
    },
    check: {
      borderColor: Colors.white,
    },
    hover: {
      borderColor: Colors.white,
    },
    focus: {
      backgroundColor: Colors.white,
      borderColor: Colors.white,
    },
  },
};

const Styles = labelStyles('Indicator', {
  input: (props: Props) => [
    {
      position: 'relative',
      appearance: 'none',
      display: 'inline-block',
      borderStyle: 'solid',
      borderWidth: 2,
      borderRadius: '50%',
      width: 10,
      height: 10,
      cursor: 'pointer',
      transitionDuration: Animation.transitionDuration,
      transitionProperty: 'border-width, border-color',
      transitionTimingFunction: Animation.transitionTimingFunction,

      // creates a ring around the radio button for the focus state
      '&::before': {
        position: 'absolute',
        height: 16,
        width: 16,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        content: '""',
        opacity: 0,
        transitionDuration: Animation.transitionDuration,
        transitionProperty: 'opacity',
        transitionTimingFunction: Animation.transitionTimingFunction,
      },

      '&:checked': {
        borderWidth: 5,
      },

      '&:focus': {
        '&::before': {
          opacity: 0.25,
        },
      },

      '&:hover': {
        borderWidth: 5,
      },
    },
    withModifier(props.color, {
      borderColor: colorVariations[props.color].base.borderColor,

      '&::before': {
        backgroundColor: colorVariations[props.color].focus.backgroundColor,
      },

      '&:checked': {
        borderColor: colorVariations[props.color].check.borderColor,
      },

      '&:focus': {
        borderColor: colorVariations[props.color].focus.borderColor,
      },

      '&:hover': {
        borderColor: colorVariations[props.color].hover.borderColor,
      },
    }),
  ],
  root: (props: Props) => [
    {
      display: 'grid',
      gap: 6,
      gridTemplateColumns: `repeat(${props.count}, 10px)`,
    },
  ],
});

/**
 * Indicator component, used for navigation within a single page.
 * It's essentially a mini-paginator for in-page navigation only.
 */
export const Indicator = (props: Props) => {
  const { count, selectedIndex, onChangeIndex, ariaLabel, noun } = props;
  const [name] = useState(uniqueId('Indicator-'));

  const handleOnChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    const index = parseInt(value, 10);
    onChangeIndex(index);
  };

  return (
    <div aria-label={ariaLabel} css={Styles.root(props)} role="radiogroup">
      {Array.from({ length: count }).map((_, i) => (
        <input
          aria-label={`${noun} ${i + 1}`}
          checked={i === selectedIndex}
          css={Styles.input(props) as Interpolation}
          key={`bubble-${i}`}
          name={name}
          onChange={handleOnChange}
          type="radio"
          value={i}
        />
      ))}
    </div>
  );
};

Indicator.defaultProps = {
  noun: 'page',
};
